import Class from "./Class";
import AVLTree from "./AVLTree";
import murmur3 from "./hash/murmur3";

function hashed(string) {
    return {
        "hash": murmur3(string),
        "string": string,
        "_count": 0
    };
}

function cmp(left, right) {
    if (left.hash !== right.hash) {
        return left.hash < right.hash ? -1 : 1;
    }
    if (left.string === right.string) {
        return 0;
    }
    return left.string < right.string ? -1 : 1;
}

function find(attrs, item) {
    let lo = 0;
    let hi = attrs.length >> 1;

    while (lo < hi) {
        const mid = lo + ((hi - lo) >> 1);

        if (cmp(attrs[mid << 1], item) < 0) {
            lo = mid + 1;
        } else {
            hi = mid;
        }
    }
    return lo << 1;
}

const AHEvent = new Class({
    initialize: function(attrs) {
        this._attrs = attrs;
    },

    count: function() {
        return this._attrs.length >> 1;
    },

    values: function(key, mapFilter) {
        const item = hashed(key);
        const hash = item.hash;
        const attrs = this._attrs;
        const start = find(attrs, item);
        const end = attrs.length;

        const hasFunc = !!(mapFilter && typeof mapFilter === "function");
        const hasMap = !hasFunc && !!(mapFilter && mapFilter.map);
        const hasFilter = !hasFunc && !!(mapFilter && mapFilter.filter);

        const values = [];
        for (let i = start; i < end && attrs[i].hash === hash && attrs[i].string === key; i += 2) {
            let value = attrs[i + 1].string;
            if (hasFunc) {
                value = mapFilter(value);
            } else if (hasMap) {
                value = mapFilter.map(value);
            }

            if (hasFilter) {
                if (mapFilter.filter(value)) {
                    values.push(value);
                }
            } else if (hasFunc || hasMap) {
                // (value === value) is equal to !isNaN(value)
                if ((value !== null) && (value !== void 0) && (value === value)) {
                    values.push(value);
                }
            } else {
                values.push(value);
            }
        }
        return values;
    },

    value: function(key, _default, mapFilter) {
        const values = this.values(key, mapFilter);
        if (values.length > 0) {
            return values[0];
        }
        return arguments.length < 2 ? null : _default;
    },

    forEach: function(func, context) {
        const attrs = this._attrs;
        for (let i = 0, len = attrs.length; i < len; i += 2) {
            func.call(context, attrs[i + 1].string, attrs[i].string, this);
        }
    },

    some: function(func, context) {
        const attrs = this._attrs;
        for (let i = 0, len = attrs.length; i < len; i += 2) {
            if (func.call(context, attrs[i + 1].string, attrs[i].string, this)) {
                return true;
            }
        }
        return false;
    },

    every: function(func, context) {
        const attrs = this._attrs;
        for (let i = 0, len = attrs.length; i < len; i += 2) {
            if (!func.call(context, attrs[i + 1].string, attrs[i].string, this)) {
                return false;
            }
        }
        return true;
    },

    _free: function(keys, values) {
        const attrs = this._attrs;
        for (let i = 0, len = attrs.length; i < len; i += 2) {
            keys.dec(attrs[i]);
            values.dec(attrs[i + 1]);
        }
    }
});

const InternTree = new Class({
    Extends: AVLTree,

    cmp: cmp,

    inc: function(string) {
        const item = hashed(string);
        const node = this.find(item) || this.insert(item);
        const value = node.value();
        value._count += 1;
        return value;
    },

    dec: function(item) {
        const node = this.find(item);
        if (node === null) {
            return;
        }

        const value = node.value();
        if (value !== item) {
            return;
        }

        if (item._count <= 1) {
            node.remove();
        } else {
            item._count -= 1;
        }
    }
});

const _has = Object.prototype.hasOwnProperty;

const isArray = Array.isArray;

function sortCmp(left, right) {
    return cmp(left.key, right.key);
}

function itemize(obj) {
    const items = [];

    if (obj instanceof AHEvent) {
        obj.forEach(function(value, key) {
            items.push({
                "key": key,
                "value": value
            });
        });
    } else {
        Object.keys(obj).forEach(function(key) {
            const values = obj[key];

            if (!isArray(values)) {
                items.push({
                    "key": key,
                    "value": values
                });
            } else {
                for (let i = 0, len = values.length; i < len; i++) {
                    items.push({
                        "key": key,
                        "value": values[i]
                    });
                }
            }
        });
    }

    return items;
}

const AHEventContainer = new Class({
    initialize: function() {
        this._keys = new InternTree();
        this._values = new InternTree();
    },

    create: function(obj) {
        const items = itemize(obj).map(function(item) {
            return {
                "key": this._keys.inc(item.key),
                "value": this._values.inc(item.value)
            };
        }, this);

        const attrs = [];
        items.sort(sortCmp).forEach(function(item) {
            attrs.push(item.key);
            attrs.push(item.value);
        });
        return new AHEvent(attrs);
    },

    free: function(event) {
        event._free(this._keys, this._values);
    }
});

function fromElements(elements, func, ctx) {
    for (let i = 0, eLength = elements.length; i < eLength; i++) {
        const element = elements[i];
        if (!element.tagName) {
            continue;
        }
        if (element.tagName.toLowerCase() !== "event") {
            continue;
        }
        if (element.getAttribute("xmlns") !== "abusehelper#event") {
            continue;
        }

        const attrs = {};
        const children = element.childNodes;
        for (let j = 0, cLength = children.length; j < cLength; j++) {
            const child = children[j];
            if (!child.tagName) {
                continue;
            }
            if (child.tagName.toLowerCase() !== "attr") {
                continue;
            }

            const key = child.getAttribute("key");
            const value = child.getAttribute("value");

            if (key !== null && value !== null) {
                if (!_has.call(attrs, key)) {
                    attrs[key] = [value];
                } else {
                    attrs[key].push(value);
                }
            }
        }
        func.call(ctx, attrs);
    }
}

function toElements(event) {
    const nonXML = /[\x00-\x08\x0b\x0c\x0e-\x1f\ud800-\udfff\ufffe\uffff]/g;
    const replace = function(string) {
        return string.replace(nonXML, "\ufffd");
    };

    const items = itemize(event);

    const element = document.createElement("event");
    element.setAttribute("xmlns", "abusehelper#event");
    items.forEach(function(item) {
        const attr = document.createElement("attr");
        attr.setAttribute("key", replace(item.key));
        attr.setAttribute("value", replace(item.value));
        element.appendChild(attr);
    });

    const attrs = {};
    items.forEach(function(item) {
        attrs[item.key] = [];
    });
    items.forEach(function(item) {
        attrs[item.key].push(item.value);
    });
    Object.keys(attrs).forEach(function(key) {
        const values = attrs[key];
        if (values.length === 1) {
            attrs[key] = values[0];
        }
    });
    const body = document.createElement("body");
    body.textContent = replace(JSON.stringify(attrs));

    return [body, element];
}

function wrapEvent(obj) {
    return (new AHEventContainer()).create(obj || {});
}

export {
    toElements,
    fromElements,
    AHEventContainer,
    wrapEvent as AHEvent
};



// WEBPACK FOOTER //
// ./src/abusesa-js/AHEvent.js